import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  auth, 
  loginWithGoogle as fbLoginWithGoogle, 
  logoutUser as fbLogoutUser, 
  isEmailAllowed, 
  getAllowedEmails,
  onAuthStateChanged 
} from '../services/firebase';
import { AuthUser } from '../types';
import { soundEffects } from '../services/audio';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAllowed: boolean;
  allowedEmails: string[];
  authError: string | null;
  guestShareToken: string | null;
  clearAuthError: () => void;
  exitGuestMode: () => void;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  loginAsDemoUser: (asAuthorized?: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [guestShareToken, setGuestShareToken] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('share')?.trim();
      return token || null;
    }
    return null;
  });
  const allowedEmails = getAllowedEmails();

  const clearAuthError = () => {
    setAuthError(null);
  };

  const exitGuestMode = () => {
    soundEffects.playClick();
    setGuestShareToken(null);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('share');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  };

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const allowed = isEmailAllowed(fbUser.email);
        const token = await fbUser.getIdToken().catch(() => undefined);
        setUser({
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          photoURL: fbUser.photoURL,
          isAllowed: allowed,
          token
        });
        if (!allowed && !guestShareToken) {
          soundEffects.playAlert();
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [guestShareToken]);

  const loginWithGoogle = async () => {
    try {
      setAuthError(null);
      soundEffects.playClick();
      const { user: fbUser, isAllowed: allowed } = await fbLoginWithGoogle();
      const token = await fbUser.getIdToken().catch(() => undefined);
      setUser({
        uid: fbUser.uid,
        email: fbUser.email,
        displayName: fbUser.displayName,
        photoURL: fbUser.photoURL,
        isAllowed: allowed,
        token
      });
      if (allowed) {
        soundEffects.playScan();
      } else {
        soundEffects.playAlert();
      }
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User voluntarily dismissed popup
        return;
      }

      soundEffects.playAlert();
      console.error('Firebase Auth Error:', err);

      if (err.code === 'auth/configuration-not-found') {
        setAuthError('Firebase Authentication is not activated on this project. Please deploy auth settings by running: npm run firebase:auth');
      } else if (err.code === 'auth/unauthorized-domain') {
        setAuthError('This domain is not listed in Authorized Domains. Please add it to VITE_AUTHORIZED_DOMAINS in .env and run: npm run firebase:auth');
      } else if (err.code === 'auth/popup-blocked') {
        setAuthError('Sign-in popup was blocked by your browser. Please allow popups for this site and try again.');
      } else if (err.code === 'auth/network-request-failed') {
        setAuthError('Network communication error. Please check your internet connection.');
      } else if (err.code === 'auth/invalid-api-key') {
        setAuthError('Invalid Firebase API key. Please check your VITE_FIREBASE_API_KEY in .env.');
      } else {
        setAuthError(err.message || 'An error occurred during authentication. Please check console.');
      }
    }
  };

  const logout = async () => {
    soundEffects.playClick();
    await fbLogoutUser();
    setUser(null);
    setAuthError(null);
  };

  const loginAsDemoUser = (asAuthorized: boolean = true) => {
    soundEffects.playClick();
    const demoEmail = asAuthorized 
      ? (allowedEmails[0] || 'admin@pokedex.dev') 
      : 'unauthorized_trainer@pokemon.com';
    const allowed = isEmailAllowed(demoEmail);
    
    setUser({
      uid: asAuthorized ? 'demo-authorized-uid-123' : 'demo-unauthorized-uid-999',
      email: demoEmail,
      displayName: asAuthorized ? 'Authorized Trainer' : 'Guest Visitor',
      photoURL: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
      isAllowed: allowed,
      token: 'demo-jwt-bearer-token-connectrpc'
    });

    if (allowed) {
      soundEffects.playScan();
    } else {
      soundEffects.playAlert();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAllowed: user ? user.isAllowed : false,
        allowedEmails,
        authError,
        guestShareToken,
        clearAuthError,
        exitGuestMode,
        loginWithGoogle,
        logout,
        loginAsDemoUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
